import React from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Grid,
  GridItem,
  Input,
  Select,
  Textarea,
  Button,
  Text,
  Progress,
  Badge,
  Link,
  useToast,
} from '@chakra-ui/react';

import { createIndividualJob, getJobStatus, previewScript } from 'api/administrador/studioIndividual';

const PROGRAM_TYPES = [
  { value: 'grade', label: 'Bachillerato' },
  { value: 'technical_career', label: 'Carrera técnica' },
  { value: 'course', label: 'Curso' },
];

const AUDIENCE_TARGETS = [
  { value: 'primaria', label: 'Primaria (1° a 5°)' },
  { value: 'secundaria', label: 'Secundaria (6° a 9°)' },
  { value: 'media', label: 'Media (10° a 11°)' },
  { value: 'tecnica', label: 'Carrera técnica' },
  { value: 'curso_general', label: 'Curso general' },
  { value: 'curso_ejecutivo', label: 'Curso ejecutivo' },
];

const STATE_LABEL = {
  queued: 'En cola',
  generating_script: 'Generando guion (LLM)',
  generating_audio: 'Generando audio (TTS)',
  rendering: 'Renderizando video',
  uploading: 'Subiendo a DigitalOcean Spaces',
  completed: 'Completado',
  failed: 'Falló',
};

const FormSchema = Yup.object().shape({
  material_id: Yup.number().positive('Debe ser positivo').required('Requerido'),
  topic: Yup.string().min(3, 'Muy corto').required('Requerido'),
  subject: Yup.string().required('Requerido'),
  program_type: Yup.string().required('Requerido'),
  grade_label: Yup.string().when('program_type', {
    is: 'grade',
    then: (schema) => schema.required('Requerido para bachillerato (ej: Séptimo, Décimo)'),
  }),
  program_name: Yup.string().when('program_type', {
    is: (val) => val === 'technical_career' || val === 'course',
    then: (schema) => schema.required('Requerido'),
  }),
  audience_target: Yup.string().required('Requerido'),
});

export default function StudioIndividual() {
  const toast = useToast();
  const [job, setJob] = React.useState(null);
  const [scriptDraft, setScriptDraft] = React.useState('');
  const [formSnapshot, setFormSnapshot] = React.useState(null);
  const [generatingScript, setGeneratingScript] = React.useState(false);
  const pollRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await getJobStatus(jobId);
        setJob(status);
        if (status.state === 'completed' || status.state === 'failed') {
          clearInterval(pollRef.current);
        }
      } catch (error) {
        clearInterval(pollRef.current);
        toast({
          title: 'No se pudo consultar el estado del job',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    }, 4000);
  };

  const handleGenerateScript = async (values, actions) => {
    try {
      setJob(null);
      const response = await previewScript(values);
      setScriptDraft(JSON.stringify(response.script, null, 2));
      setFormSnapshot(values);
      toast({
        title: 'Guion generado — revísalo antes de continuar',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'No se pudo generar el guion',
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleConfirmAndRender = async () => {
    let parsedScript;
    try {
      parsedScript = JSON.parse(scriptDraft);
    } catch (error) {
      toast({
        title: 'El guion editado no es JSON válido',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await createIndividualJob(formSnapshot, parsedScript);
      const jobId = response.jobs?.[0]?.job_id;
      if (!jobId) throw new Error('El worker no devolvió job_id');

      setJob({ job_id: jobId, state: 'queued', progress: 0 });
      pollJob(jobId);

      toast({
        title: 'Job encolado en Klazia Studio',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'No se pudo encolar el job',
        description: error?.response?.data?.error || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDiscardDraft = () => {
    setScriptDraft('');
    setFormSnapshot(null);
  };

  return (
    <Box p={4}>
      <Text fontSize="xl" fontWeight="700" mb={6}>
        Klazia Studio — Modo Individual
      </Text>

      <Grid templateColumns="repeat(2, 1fr)" gap={8}>
        <GridItem colSpan={1}>
          <Formik
            initialValues={{
              material_id: '',
              topic: '',
              subject: '',
              program_type: 'grade',
              grade_label: '',
              program_name: '',
              audience_target: 'secundaria',
            }}
            validationSchema={FormSchema}
            onSubmit={handleGenerateScript}
          >
            {({ values, isSubmitting }) => (
              <Form>
                <Box fontWeight="500">Material ID (lección a restaurar)</Box>
                <Field name="material_id" as={Input} type="number" marginTop="6px" marginBottom="12px" />

                <Box fontWeight="500">Tema de la clase</Box>
                <Field
                  name="topic"
                  as={Input}
                  placeholder="Ej: Ecuaciones lineales de primer grado"
                  marginTop="6px"
                  marginBottom="12px"
                />

                <Box fontWeight="500">Materia</Box>
                <Field name="subject" as={Input} placeholder="Ej: Matemáticas" marginTop="6px" marginBottom="12px" />

                <Box fontWeight="500">Tipo de programa</Box>
                <Field name="program_type" as={Select} marginTop="6px" marginBottom="12px">
                  {PROGRAM_TYPES.map((opt) => (
                    <option value={opt.value} key={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Field>

                {values.program_type === 'grade' && (
                  <>
                    <Box fontWeight="500">Grado (nombre real en klazia-api)</Box>
                    <Field
                      name="grade_label"
                      as={Input}
                      placeholder="Ej: Séptimo, Décimo, Ciclo: Medio"
                      marginTop="6px"
                      marginBottom="12px"
                    />
                  </>
                )}

                {(values.program_type === 'technical_career' || values.program_type === 'course') && (
                  <>
                    <Box fontWeight="500">
                      {values.program_type === 'technical_career' ? 'Nombre de la carrera técnica' : 'Nombre del curso'}
                    </Box>
                    <Field name="program_name" as={Input} marginTop="6px" marginBottom="12px" />
                  </>
                )}

                <Box fontWeight="500">Audiencia (voz TTS)</Box>
                <Field name="audience_target" as={Select} marginTop="6px" marginBottom="12px">
                  {AUDIENCE_TARGETS.map((opt) => (
                    <option value={opt.value} key={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Field>

                <Button
                  type="submit"
                  colorScheme="messenger"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  marginTop="10px"
                >
                  {scriptDraft ? 'Regenerar guion' : '1. Generar guion'}
                </Button>
              </Form>
            )}
          </Formik>

          {scriptDraft && (
            <Box mt={6} borderWidth="1px" borderRadius="8px" p={4}>
              <Text fontWeight="600" mb={2}>
                2. Revisa y edita el guion antes de renderizar
              </Text>
              <Textarea
                value={scriptDraft}
                onChange={(e) => setScriptDraft(e.target.value)}
                fontFamily="mono"
                fontSize="sm"
                minH="320px"
              />
              <Button mt={3} mr={3} colorScheme="green" onClick={handleConfirmAndRender}>
                Confirmar y generar video
              </Button>
              <Button mt={3} variant="ghost" onClick={handleDiscardDraft}>
                Descartar
              </Button>
            </Box>
          )}
        </GridItem>

        <GridItem colSpan={1}>
          {job && (
            <Box borderWidth="1px" borderRadius="8px" p={5}>
              <Text fontWeight="600" mb={2}>
                Job {job.job_id}
              </Text>
              <Badge colorScheme={job.state === 'failed' ? 'red' : job.state === 'completed' ? 'green' : 'purple'}>
                {STATE_LABEL[job.state] || job.state}
              </Badge>
              <Progress
                mt={4}
                value={job.progress || 0}
                colorScheme={job.state === 'failed' ? 'red' : 'purple'}
                borderRadius="4px"
              />

              {job.state === 'completed' && job.result?.video?.source && (
                <Box mt={4}>
                  <Text fontWeight="500">Video generado:</Text>
                  <Link color="blue.500" href={job.result.video.source} target="_blank">
                    {job.result.video.source}
                  </Link>
                </Box>
              )}

              {job.state === 'failed' && (
                <Text mt={4} color="red.500">
                  {job.failed_reason || 'El job falló durante el procesamiento.'}
                </Text>
              )}
            </Box>
          )}
        </GridItem>
      </Grid>
    </Box>
  );
}
